/**
 * stepViewFor — which of the six step views a run is currently on.
 *
 * Story 26739. The step views are the "what do I do right now" surface: one
 * pane per moment in the run, rather than one drawer that changes shape. This
 * module is the ONLY place that decides which one is showing.
 *
 * IT LAYERS ON `pickDrawerStatusFamily`; IT DOES NOT REPLACE IT. The drawer's
 * status family is already the shared vocabulary for "what kind of thing is
 * this run doing" — it owns the `pending_edocs`-before-`hitlQuestion` ordering,
 * the "null status is loading, not unknown" rule, and the dev warning for a
 * server-added status. A second switch over raw status strings would be a
 * second copy of all three, and the first one to drift would be this one,
 * because it is the one nobody reads while debugging a status bug.
 *
 * So: family first, then the two refinements the family cannot make —
 * whether a carrier-evidence decision is live (a fact about the RUN, not about
 * the parked status), and whether a HITL gate is a routine decision or a
 * report that something went wrong.
 *
 * Pure. No React, no Office.js, no Remix server import — it is imported by
 * BOTH hosts (design Constraint 20) and by the add-in's Vite bundle.
 */

import { pickDrawerStatusFamily } from "../drawer/pickDrawerStatusFamily";

/**
 * The six step views.
 *
 * `brief` and `exception` are BOTH "answer a question"; they differ in what
 * the pane leads with. A brief leads with what ora is about to do and asks for
 * a go-ahead. An exception leads with what ora ran into and asks how to get
 * past it — the same pills, a different first paragraph, and a restatement of
 * the request because the person reading it has lost the thread.
 */
export type StepViewName =
  | "brief"
  | "running"
  | "exception"
  | "carrier_packet"
  | "reply_ready"
  | "done";

/**
 * The HITL gates that are ora reporting a PROBLEM rather than asking for a
 * routine decision — the drift and interlock gates.
 *
 * PROPOSED, PENDING RATIFICATION (design decision D1, `tasks/26735-decision-ui-design.md`).
 * Seeded from the checkpoints declared in the workflows repo
 * (`graphs/orchestrations/carrier_mortgagee_update.py`,
 * `graphs/orchestrations/_carrier_mortgagee_shared_models.py`,
 * `epic_shared/_epic_mfa_phases_shared.py`), classified by what the gate's own
 * `pending_action` says:
 *
 *   drift      — what ora found is not what it expected, so a person has to
 *                confirm or redirect it:
 *                  `verify_epic_customer`        "Confirm the correct customer was located"
 *                  `verify_policy_detail`        "Confirm the correct policy was opened"
 *                  `existing_change_ack`         "An in-process endorsement already exists — resume it?"
 *                  `additional_interest_picker`  "Pick the Additional Interest row (multiple rows present)"
 *   interlock  — something outside the run is blocking it:
 *                  `record_locked`               "Another employee has this record open in Epic"
 *                  `relogin_iam_credentials`     "…the session expired while waiting for you"
 *                  `epic_mfa_terminal_stuck`     "Your Microsoft sign-in got stuck"
 *
 * DELIBERATELY NOT HERE — these are routine decisions and render as briefs:
 *   `confirm_mortgagee`, `confirm_client`, `disambiguate_policy`, `review_draft`,
 *   `before_submit`, the ordinary MFA gates (`epic_mfa_pending`,
 *   `epic_mfa_method_choice`, `epic_mfa_code_entry`).
 * And these are their own views, reached before this set is consulted:
 *   the packet gates (`carrier_action_instruction`, `carrier_manual_change`,
 *   `carrier_portal_complete`) and the document waits (`edoc_wait`,
 *   `decpage_wait`) — `carrier_packet`.
 *
 * There is no substrate-side list to import: the substrate groups these
 * checkpoints by `PACKET_CHECKPOINTS` and `AUTO_RESUME_CHECKPOINT_IDS` and has
 * no "this one is an exception" marker at all. Until it does, this list is the
 * discriminator and it is a JUDGEMENT, which is why it is written down with
 * the evidence for each member rather than left implicit in a condition.
 *
 * OPEN DISAGREEMENT TO SETTLE AT RATIFICATION. `tasks/26735-decision-ui-design.md`
 * proposes a deliberately narrower D1 seed — `{"carrier_manual_change"}` alone,
 * expanded only once a second real example appears. This list disagrees on
 * both halves:
 *
 *   - `carrier_manual_change` is one of the substrate's own
 *     `PACKET_CHECKPOINTS` ("type the change into the carrier portal"), so it
 *     resolves to `carrier_packet` before this set is ever consulted. As the
 *     ONLY member it would leave the exception view unreachable in practice.
 *   - The seven above are not speculative: each is a declared checkpoint whose
 *     own `pending_action` string describes drift or a blocker, quoted above.
 *
 * Whoever ratifies D1 should pick one of the two lists deliberately. Narrowing
 * to the design's seed is a one-line change here and nothing else.
 */
/* ===========================================================================
 * !!  HAND-MAINTAINED TRIAGE LIST — READ BEFORE ADDING A WORKFLOW OR A GATE  !!
 *
 * EVERY new workflow, and EVERY new checkpoint added to an existing workflow,
 * MUST BE TRIAGED HERE: exception (drift / interlock — ora ran into something)
 * or routine brief (ora is asking for a go-ahead).
 *
 * THERE IS NO COMPILE-TIME AND NO RUNTIME SIGNAL IF YOU SKIP THIS. The
 * substrate carries no exception marker (see the block above), so a genuinely
 * urgent gate that nobody added to this set renders as a calm routine brief —
 * no type error, no warning, no failing build. The only thing standing in the
 * way is the triage contract test in
 * `app/testing/common/workflows/steps/stepViewFor.test.ts`, which pins this
 * set's exact membership AND requires every checkpoint id this repo knows
 * about to be named in one bucket or the other. If that test failed and you
 * got here from it: that is it working — decide which bucket the new gate
 * belongs in and say so in both places.
 *
 * Context: PR 8688 review finding #31.
 * =========================================================================== */
export const EXCEPTION_CHECKPOINTS: ReadonlySet<string> = new Set([
  "carrier_mortgagee_update.verify_epic_customer",
  "carrier_mortgagee_update.verify_policy_detail",
  "carrier_mortgagee_update.existing_change_ack",
  "carrier_mortgagee_update.additional_interest_picker",
  "carrier_mortgagee_update.record_locked",
  "carrier_mortgagee_update.relogin_iam_credentials",
  "carrier_mortgagee_update.epic_mfa_terminal_stuck",
]);

/**
 * The checkpoint identity carried on a HITL question.
 *
 * The substrate writes the same value under BOTH `checkpoint_id` and `kind`
 * (see `tools/waiting_queue.py`'s AUTO_RESUME_KINDS note), and older payloads
 * carry only one of them, so both are read. Returns `null` rather than a
 * guess when neither is a non-empty string.
 */
export function readCheckpointId(hitlQuestion: unknown): string | null {
  if (!hitlQuestion || typeof hitlQuestion !== "object") return null;
  const q = hitlQuestion as Record<string, unknown>;
  for (const key of ["checkpoint_id", "kind"]) {
    const v = q[key];
    if (typeof v === "string" && v.trim() !== "") return v.trim();
  }
  return null;
}

/**
 * True when this gate is an exception rather than a routine decision.
 *
 * Matches the fully-qualified id first. A bare id (`"record_locked"`) also
 * matches, because a checkpoint reaches this surface fully-qualified from the
 * orchestration and BARE from some question payloads, and the two spellings
 * are the same gate. The bare comparison is against the suffix of a declared
 * member only — an arbitrary bare id never matches something it does not name.
 *
 * A checkpoint this function has never heard of is `false` — a routine brief.
 * That default is SILENT by construction, which is why membership of
 * {@link EXCEPTION_CHECKPOINTS} is pinned by the triage contract test rather
 * than left to whoever adds the next gate remembering it exists.
 */
export function isExceptionCheckpoint(
  checkpointId: string | null | undefined,
): boolean {
  if (!checkpointId) return false;
  if (EXCEPTION_CHECKPOINTS.has(checkpointId)) return true;
  for (const declared of EXCEPTION_CHECKPOINTS) {
    const dot = declared.lastIndexOf(".");
    if (dot >= 0 && declared.slice(dot + 1) === checkpointId) return true;
  }
  return false;
}

/**
 * The gates where the carrier PACKET is the question being asked — the run is
 * parked and a person has to act on the carrier's side. Mirrors the substrate's
 * `facts.PACKET_CHECKPOINTS`, plus the `edoc_wait` document wait and the
 * `carrier_change_manual` spelling that `shouldShowCarrierEvidenceCard` already
 * accepts, because both spellings reach this surface from different payloads.
 */
export const PACKET_CHECKPOINTS: ReadonlySet<string> = new Set([
  "carrier_action_instruction",
  "carrier_manual_change",
  "carrier_change_manual",
  "carrier_portal_complete",
  "edoc_wait",
]);

/** True when this gate is the carrier hand-off itself. Same bare-id tolerance
 *  as {@link isExceptionCheckpoint}, for the same reason. */
export function isPacketCheckpoint(
  checkpointId: string | null | undefined,
): boolean {
  if (!checkpointId) return false;
  if (PACKET_CHECKPOINTS.has(checkpointId)) return true;
  const dot = checkpointId.lastIndexOf(".");
  return dot >= 0 && PACKET_CHECKPOINTS.has(checkpointId.slice(dot + 1));
}

export interface StepViewInput {
  /** The run's live status string, or null while it is still loading. */
  status?: string | null;
  /** The live HITL question, when one is pending. */
  hitlQuestion?: unknown;
  /**
   * The carrier-evidence decision, when the run carries one. Read from
   * `readEvidenceDecision(status)` by the host — passed in rather than derived
   * here so this module stays free of the phase model.
   *
   * Present means "this run's evidence branch is live". It no longer OUTRANKS
   * the status: a decision recorded early (the Servicer choosing "I'll supply
   * it" before the run parks) is a fact about the run, but it is not a demand,
   * and letting it pick the view handed the whole pane to a step that was not
   * blocking — a busy run showed a yellow "Needs you" while ora worked (run
   * 0e29f86f, 2026-09-10). It now only selects the packet view at the gates
   * where the packet IS the question.
   *
   * This costs the early-override path nothing: the evidence CARD has its own
   * visibility rule (`shouldShowCarrierEvidenceCard`) and its own slot in every
   * drawer body, so staging a dec page early is still one click from the
   * running view.
   */
  evidenceDecision?: unknown;
  /**
   * The prepared brief, when the pane is showing one and no run exists yet.
   * Only consulted when there is no status at all — once a run is live, the
   * run decides.
   */
  brief?: unknown;
}

/**
 * The workflows the step views are written for: the mortgagee updates. Every
 * sentence they show is about one of these runs — "… is now the mortgagee of
 * record", "the endorsement is recorded in Epic for the Servicer". Any other
 * workflow keeps the drawer body it had before the step views: gated on the
 * flag alone, they told a cancelled quote comparison its endorsement was in
 * Epic (operator, 2026-09-28).
 */
export const STEP_VIEW_WORKFLOWS: ReadonlySet<string> = new Set([
  "carrier_mortgagee_update",
  "carrier_mortgagee_update_v2",
  "mortgagee_update_v1_2",
]);

/** Whether a run of `workflowId` gets the step views (when their flag is on). */
export function hasStepViews(workflowId: string | null | undefined): boolean {
  return workflowId != null && STEP_VIEW_WORKFLOWS.has(workflowId);
}

/**
 * The step view for a run, derived fresh from its live status on every render.
 *
 * NEVER cache this in state or derive it from a drawer "mode": the add-in's
 * terminal push arrives as a status change on the same mounted tree, and a
 * view held in state would keep showing `running` for a run that finished
 * thirty seconds ago.
 */
export function stepViewFor(input: StepViewInput): StepViewName {
  const { status, hitlQuestion, evidenceDecision, brief } = input;

  // THE RUN'S STATUS DECIDES. Nothing is consulted before it — see the
  // `evidenceDecision` prop doc for why an early evidence decision used to win
  // here and why it must not.
  const family = pickDrawerStatusFamily(status, hitlQuestion);

  switch (family) {
    case "pending_edocs":
      return "carrier_packet";
    case "pending_email_send":
      return "reply_ready";
    case "hitl": {
      const checkpoint = readCheckpointId(hitlQuestion);
      // Parked ON the carrier hand-off with a live evidence branch: the packet
      // is literally what is being asked, so it is the view.
      if (evidenceDecision != null && isPacketCheckpoint(checkpoint)) {
        return "carrier_packet";
      }
      return isExceptionCheckpoint(checkpoint) ? "exception" : "brief";
    }
    case "active":
      // No status at all + a prepared brief = the pre-launch pane: ora has not
      // started, it is showing what it would do and asking for a go-ahead.
      if (status == null && brief != null) return "brief";
      return "running";
    default:
      // completed | terminal_error | terminal_cancelled — all "done". A failed
      // run is still finished; what went wrong is the done view's own content,
      // not a seventh view.
      return "done";
  }
}

export default stepViewFor;
